import { useEffect, useId, useRef, useState } from 'react'
import { X, AlertCircle, CheckCircle, Info, Download } from 'lucide-react'
import clsx from 'clsx'

// ─── Spinner ──────────────────────────────────────────────────────
export function Spinner({ size = 'md', className = '' }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' }
  return (
    <div className={clsx(
      'border-2 border-brand-500 border-t-transparent rounded-full animate-spin',
      sizes[size], className
    )} />
  )
}

// ─── LoadingScreen ────────────────────────────────────────────────
export function LoadingScreen() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" />
    </div>
  )
}

// ─── Badge ────────────────────────────────────────────────────────
const BADGE_COLOR_MAP = {
  green: 'badge-green', blue: 'badge-blue', yellow: 'badge-yellow',
  red: 'badge-red', gray: 'badge-gray', purple: 'badge-purple',
  active: 'badge-green', paid: 'badge-green', completed: 'badge-green',
  available: 'badge-green',
  past_due: 'badge-yellow', sent: 'badge-blue', pending: 'badge-yellow',
  overlocked: 'badge-red', failed: 'badge-red', overdue: 'badge-red',
  occupied: 'badge-blue', reserved: 'badge-blue',
  moved_out: 'badge-gray', void: 'badge-gray', draft: 'badge-gray',
  refunded: 'badge-purple', maintenance: 'badge-gray',
}

export function Badge({ color, children }) {
  const cls = BADGE_COLOR_MAP[color] || BADGE_COLOR_MAP[children?.toString().toLowerCase()] || 'badge-gray'
  return <span className={cls}>{children}</span>
}

// ─── Modal ────────────────────────────────────────────────────────
//
// A11y contract:
//   - role="dialog" + aria-modal="true" so screen readers announce it as
//     an in-context surface instead of just anouncing the whole document.
//   - Escape closes (bound to keydown on document while open).
//   - Focus is moved into the panel on open and returned to whatever had
//     focus before on close, so keyboard users don't get stranded.
//   - Focus is trapped inside the panel — Tab / Shift+Tab cycle through
//     the panel's focusables and do not escape to the page behind.
//   - Body scroll is locked while open so mobile users don't accidentally
//     scroll the page behind the modal.
export function Modal({ open, onClose, title, children, size = 'md' }) {
  const panelRef = useRef(null)
  const previousFocusRef = useRef(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return

    previousFocusRef.current = document.activeElement

    // Focus the first focusable in the panel; fall back to the panel
    // itself (which is focusable via tabIndex=-1).
    requestAnimationFrame(() => {
      const focusables = getFocusables(panelRef.current)
      const target = focusables[0] ?? panelRef.current
      target?.focus?.()
    })

    // Lock body scroll while open.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose?.()
        return
      }
      if (e.key !== 'Tab') return
      const focusables = getFocusables(panelRef.current)
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusables[0]
      const last  = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      previousFocusRef.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={clsx(
          'relative bg-white rounded-2xl shadow-2xl w-full flex flex-col max-h-[calc(100vh-2rem)] focus:outline-none',
          widths[size],
        )}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 id={titleId} className="text-base font-display font-700 text-gray-900">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg" aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

// Enumerate all focusable descendants of a container. Kept small; the
// selector list covers what our Modal children actually use.
function getFocusables(root) {
  if (!root) return []
  return Array.from(root.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((el) => el.offsetParent !== null || el === document.activeElement)
}

// ─── Alert ────────────────────────────────────────────────────────
//
// Errors get role="alert" so screen readers interrupt and announce
// immediately. Success/info use role="status" (aria-live polite) so
// they're announced after any in-progress speech - not disruptive but
// still not missed.
export function Alert({ type = 'info', message }) {
  const styles = {
    info:    { cls: 'bg-blue-50 text-blue-800 border-blue-200',   Icon: Info },
    success: { cls: 'bg-green-50 text-green-800 border-green-200', Icon: CheckCircle },
    error:   { cls: 'bg-red-50 text-red-800 border-red-200',      Icon: AlertCircle },
  }
  const { cls, Icon } = styles[type] || styles.info
  const isError = type === 'error'
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={clsx('flex items-start gap-2.5 px-4 py-3 rounded-lg border text-sm', cls)}
    >
      <Icon size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

// ─── EmptyState ───────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {Icon && (
        <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
          <Icon size={26} className="text-gray-400" />
        </div>
      )}
      <h3 className="text-sm font-semibold text-gray-700 mb-1">{title}</h3>
      {description && <p className="text-sm text-gray-400 max-w-xs">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ─── StatCard ─────────────────────────────────────────────────────
export function StatCard({ label, value, icon: Icon, color = 'brand', trend, trendLabel }) {
  const colors = {
    brand:  'bg-brand-50 text-brand-600',
    green:  'bg-emerald-50 text-emerald-600',
    blue:   'bg-blue-50 text-blue-600',
    red:    'bg-red-50 text-red-600',
    yellow: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  }
  return (
    <div className="stat-card">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
        {Icon && (
          <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', colors[color])}>
            <Icon size={15} />
          </div>
        )}
      </div>
      <p className="text-2xl font-display font-700 text-gray-900">{value}</p>
      {trendLabel && (
        <p className={clsx('text-xs mt-1', trend >= 0 ? 'text-emerald-600' : 'text-red-500')}>
          {trend >= 0 ? '↑' : '↓'} {trendLabel}
        </p>
      )}
    </div>
  )
}

// ─── PageHeader ───────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

// ─── Pagination ───────────────────────────────────────────────────
export function Pagination({ meta, onPage }) {
  if (!meta || meta.last_page <= 1) return null
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
      <p className="text-xs text-gray-400">
        Showing {(meta.current_page - 1) * meta.per_page + 1}–
        {Math.min(meta.current_page * meta.per_page, meta.total)} of {meta.total}
      </p>
      <div className="flex items-center gap-1">
        {Array.from({ length: meta.last_page }, (_, i) => i + 1).map(p => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={clsx(
              'w-7 h-7 rounded text-xs font-medium transition-colors',
              p === meta.current_page
                ? 'bg-brand-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── ConfirmModal ─────────────────────────────────────────────────
export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false, loading = false }) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-gray-600 mb-5">{message}</p>
      <div className="flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className={danger ? 'btn-danger' : 'btn-primary'}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" /> : confirmLabel}
        </button>
      </div>
    </Modal>
  )
}

// ─── ExportButton ─────────────────────────────────────────────────
// Wraps an async download action with its own busy state. `onExport`
// should return a promise (e.g. one of the exportApi calls).
export function ExportButton({ onExport, label = 'Export CSV', icon: Icon = Download }) {
  const [busy, setBusy] = useState(false)

  const run = async () => {
    setBusy(true)
    try {
      await onExport()
    } catch {
      alert('Export failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <button className="btn-secondary" onClick={run} disabled={busy}>
      {busy ? <Spinner size="sm" /> : <Icon size={14} />} {label}
    </button>
  )
}

// ─── FormField ────────────────────────────────────────────────────
export function FormField({ label, error, children, required }) {
  return (
    <div>
      <label className="label">
        {label} {required && <span className="text-red-400 font-normal">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}

// ─── Table ────────────────────────────────────────────────────────
//
// Table renders as a real <table> on md+ (screen readers happily read
// column-header context) and as a stack of cards on mobile, where each
// row becomes a card with the column label prefixed to each cell.
// Same data + columns config in both modes - no per-page changes needed.
//
// Rows can opt out of the mobile-card treatment by setting
// `col.hideOnMobile = true`, useful for wide "actions" columns.
export function Table({ columns, data, onRowClick, emptyState }) {
  if (!data?.length && emptyState) return emptyState

  return (
    <>
      {/* Desktop / tablet - real table */}
      <div className="hidden md:block overflow-x-auto" role="region" tabIndex={0} aria-label="Data table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {columns.map((col, i) => (
                <th
                  key={i}
                  scope="col"
                  className={clsx(
                    'px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap',
                    col.className
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.map((row, ri) => (
              <tr
                key={ri}
                onClick={() => onRowClick?.(row)}
                className={clsx('table-row-hover', onRowClick && 'cursor-pointer')}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    onRowClick(row)
                  }
                }}
              >
                {columns.map((col, ci) => (
                  <td key={ci} className={clsx('px-4 py-3 text-gray-700', col.className)}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile - card stack */}
      <ul className="md:hidden space-y-2">
        {data.map((row, ri) => (
          <li
            key={ri}
            className={clsx(
              'card p-3 space-y-1.5',
              onRowClick && 'cursor-pointer hover:border-brand-300',
            )}
            onClick={() => onRowClick?.(row)}
            tabIndex={onRowClick ? 0 : undefined}
            role={onRowClick ? 'button' : undefined}
            onKeyDown={(e) => {
              if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                onRowClick(row)
              }
            }}
          >
            {columns.filter((c) => !c.hideOnMobile).map((col, ci) => (
              <div key={ci} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex-shrink-0">{col.header}</span>
                <span className="text-right text-gray-700 min-w-0 break-words">
                  {col.render ? col.render(row) : row[col.key]}
                </span>
              </div>
            ))}
          </li>
        ))}
      </ul>
    </>
  )
}
